import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { BookOpen, ExternalLink, ArrowRight, Filter } from 'lucide-react';

export const PublicationsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('ALL');
  const [selectedFormat, setSelectedFormat] = useState<string>('ALL');

  const { data: publicationsRes, isLoading } = useQuery({
    queryKey: ['public-publications', { category: selectedCategory, format: selectedFormat }],
    queryFn: () => {
      let url = '/public/publications';
      const params = new URLSearchParams();
      if (selectedCategory !== 'ALL') params.append('category', selectedCategory);
      if (selectedFormat !== 'ALL') params.append('format', selectedFormat);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const publications = publicationsRes || [];

  return (
    <div className="space-y-16 py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Header */}
      <section className="text-center space-y-4 max-w-3xl mx-auto">
        <span className="text-xs font-mono uppercase tracking-widest text-brand-600 font-bold">
          Published Works &amp; Portfolio
        </span>
        <h1 className="text-4xl sm:text-5xl font-serif font-bold text-editorial-dark">
          Literature of Enduring Truth
        </h1>
        <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
          Explore books published by For The Truth Publishing across biblical studies, church history, expository preaching, and Christian living.
        </p>
      </section>

      {/* Filter Bar */}
      <section className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-xl bg-white border border-editorial-border shadow-xs">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
          <Filter className="w-4 h-4 text-brand-600" />
          <span>Filter Catalog:</span>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          {['ALL', 'Biblical Studies', 'Theology', 'Systematic Theology', 'Christian Living'].map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 rounded-md font-semibold transition-colors ${
                selectedCategory === cat
                  ? 'bg-editorial-dark text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {cat === 'ALL' ? 'All Categories' : cat}
            </button>
          ))}
        </div>
      </section>

      {/* Catalog Grid */}
      <section>
        {isLoading ? (
          <div className="text-center py-16 text-slate-500 font-mono text-xs">Loading publications catalog...</div>
        ) : publications.length === 0 ? (
          <div className="text-center py-16 text-slate-500 text-sm">No publications match your selected filter.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {publications.map((book: any) => (
              <div
                key={book.id}
                className="group flex flex-col justify-between bg-white rounded-2xl overflow-hidden border border-editorial-border hover:border-brand-300 hover:shadow-editorial-lg transition-all"
              >
                <div className="aspect-[4/5] bg-slate-50/80 overflow-hidden relative flex items-center justify-center p-4 border-b border-slate-100">
                  <img
                    src={book.coverImage}
                    alt={book.title}
                    className="max-h-full max-w-full object-contain drop-shadow-md group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 bg-editorial-dark/90 backdrop-blur-xs text-brand-300 text-[10px] font-mono px-2.5 py-1 rounded uppercase tracking-wider font-bold border border-brand-500/30">
                    {book.format}
                  </div>
                </div>

                <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                  <div className="space-y-2">
                    <span className="text-[11px] text-brand-700 font-mono font-bold uppercase tracking-wider block">
                      {book.category}
                    </span>
                    <h3 className="font-serif text-xl font-bold text-slate-900 group-hover:text-brand-800 transition-colors">
                      {book.title}
                    </h3>
                    <p className="text-xs text-slate-500 font-medium">
                      By <strong>{book.author}</strong>
                    </p>
                    <p className="text-xs text-slate-600 line-clamp-3 leading-relaxed pt-1">
                      {book.description}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                    <Link
                      to={`/publications/${book.slug}`}
                      className="font-bold text-brand-700 hover:text-brand-800"
                    >
                      Book Details &rarr;
                    </Link>

                    {book.bookstoreUrl && (
                      <a
                        href={book.bookstoreUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-slate-700 hover:text-editorial-dark font-semibold"
                      >
                        <span>Bookstore</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

    </div>
  );
};
